import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NotificationDto, timeAgo, useNotifications } from './useNotifications';

const TYPE_ICON: Record<string, string> = {
  RecordFlagged: '⚠️', DisputeOpened: '💬', DisputeReplied: '💬', DisputeResolved: '✅', PeriodShared: '📋', PeriodLocked: '🔒',
  InvoiceIssued: '🧾', InvoiceOverdue: '⏰', PaymentRecorded: '💚', StorageLow: '💾', SyncFailures: '📡', FleetImportCompleted: '🚗', Test: '🔔',
};

/**
 * Top-bar bell with unread badge and a dropdown of the latest notifications. Clicking an item marks it read and follows its link.
 * Shared by the admin app, the partner portal and the operator PWA.
 */
export function NotificationsBell({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { unread, latest, markRead, markAllRead, refresh } = useNotifications();

  useEffect(() => {
    if (!open) return;
    void refresh();
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open, refresh]);

  const go = (n: NotificationDto) => {
    setOpen(false);
    if (!n.isRead) void markRead(n.id);
    if (n.link) navigate(n.link);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" className="gr-btn gr-btn--ghost" aria-label={t('notifications.title')} aria-expanded={open} onClick={() => setOpen((o) => !o)}
        style={{ minHeight: 36, minWidth: 40, padding: '0 8px', position: 'relative', fontSize: 18 }}>
        🔔
        {unread > 0 && (
          <span aria-label={`${unread} ${t('notifications.unread')}`} style={{ position: 'absolute', top: 2, insetInlineEnd: 2, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: 'var(--gr-error)', color: '#fff', fontSize: 11, fontWeight: 700, display: 'grid', placeItems: 'center', lineHeight: 1 }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div role="menu" className="gr-card" style={{ position: 'absolute', insetInlineEnd: 0, top: 'calc(100% + 6px)', width: compact ? 'min(92vw, 360px)' : 380, maxHeight: '70vh', overflow: 'auto', zIndex: 50, padding: 0, boxShadow: 'var(--gr-shadow-card)' }}>
          <div className="gr-row" style={{ padding: '10px 14px', borderBottom: '1px solid var(--gr-border)' }}>
            <strong>{t('notifications.title')}</strong>
            <span className="gr-topbar__spacer" />
            {unread > 0 && <button type="button" className="gr-btn gr-btn--ghost" style={{ minHeight: 28, fontSize: 13 }} onClick={() => void markAllRead()}>{t('notifications.mark_all_read')}</button>}
          </div>
          {latest.length === 0 && <p className="gr-muted" style={{ padding: 18, textAlign: 'center' }}>{t('notifications.empty')}</p>}
          {latest.map((n) => (
            <button key={n.id} type="button" role="menuitem" onClick={() => go(n)}
              style={{ display: 'flex', gap: 10, width: '100%', textAlign: 'start', padding: '10px 14px', border: 0, borderBottom: '1px solid var(--gr-border)', background: n.isRead ? 'transparent' : 'color-mix(in srgb, var(--gr-primary) 7%, transparent)', cursor: 'pointer', color: 'inherit', font: 'inherit' }}>
              <span style={{ fontSize: 18, lineHeight: 1.2 }}>{TYPE_ICON[n.type] ?? '🔔'}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  <strong style={{ fontWeight: n.isRead ? 500 : 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</strong>
                  <span className="gr-muted" style={{ fontSize: 12, flexShrink: 0 }}>{timeAgo(n.createdAtUtc)}</span>
                </span>
                <span className="gr-muted" style={{ fontSize: 13, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{n.body}</span>
              </span>
            </button>
          ))}
          <div style={{ padding: 10, textAlign: 'center' }}>
            <button type="button" className="gr-btn gr-btn--ghost" style={{ minHeight: 30, fontSize: 13 }} onClick={() => { setOpen(false); navigate('/notifications'); }}>{t('notifications.view_all')} →</button>
          </div>
        </div>
      )}
    </div>
  );
}
